import { useEffect, useState } from "react";
import { EventSourcePolyfill } from "event-source-polyfill";
import { Table, Tooltip, Typography, Button, notification } from "antd";
import { getColorForStatus, formatRupee } from "../../utils";
import { AutoCallResponseModal } from "./CallResponseModal";
import { useMutation } from "@tanstack/react-query";
import { initiateCall } from "../../services/agentController";

type BorrowerDetails = {
  borrowerName?: string;
  loanId?: string;
  dpd?: string;
  loanAmount?: number;
  dueAmount?: number;
  status?: string;
  clientName?: string;
  mobileNumber?: string;
  initialAmount?: number;
  otherCharges?: number;
  dateOfPurchase?: string;
  settlementAmount?: number;
  waiverAmount?: number;
  finalOfferAmount?: number;
  productName?: string;
  interestAmount?: number;
  dpdCount?: number;
  borrowerAge?: number;
  address?: string;
};

type StreamType = "knowlarity" | "solveesy" | null;

export const BorrowerData = ({ isCaseDetailsPending }: any) => {
  const [borrowerViewDetails, setBorrowerViewDetails] =
    useState<BorrowerDetails | null>(null);
  const [eventSource, setEventSource] = useState<EventSourcePolyfill | null>(
    null
  );
  const [isStreaming, setIsStreaming] = useState(false);
  // Tracks WHICH stream is active so you can show it in UI if needed later
  const [activeStreamType, setActiveStreamType] = useState<StreamType>(null);
  const [openCallResponseModal, setOpenCallResponseModal] = useState(false);
  const [hangupBorrowerData, setHangupBorrowerData] = useState<any>(null);
  const [caseId, setCaseId] = useState<string | null>(null);
  const [callInitiationData, setCallInitiationData] = useState({});

  const user_data_string = localStorage.getItem("userData");
  const user_data = user_data_string ? JSON.parse(user_data_string) : null;
  const email = user_data?.user_name;

 const startStreamGeneric = (endpoint: string, type: StreamType) => {
  if (activeStreamType === type) return; // already running this one, ignore

  // If a different stream is running, close it first before switching
  if (eventSource) {
    eventSource.close();
    setEventSource(null);
  }

  setIsStreaming(true);
  setActiveStreamType(type);

  const es = new EventSourcePolyfill(
    `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
    { withCredentials: true }
  );

  es.onopen = () => {
    console.log(`✅ SSE Connected (${type})`);
  };

  es.addEventListener("borrower-data", (event: any) => {
    const parsed = JSON.parse(event.data);

    switch (parsed.evenType) {
      case "AGENT_CALL":
        notification.success({ message: "Success", description: "📞 Please Receive Your Phone Call" });
        break;
      case "AGENT_ANSWER":
        notification.success({ message: "Success", description: "Agent Answered ✅" });
        break;
      case "CUSTOMER_CALL":
        notification.success({ message: "Success", description: "Borrower Phone Ringing 📞" });
        break;
      case "CUSTOMER_ANSWER":
        notification.success({ message: "Success", description: "Customer Answered 🎯" });
        break;
      case "BRIDGE":
        notification.success({
          message: "Success",
          description: `New Bridge Created For: ${parsed.borrowerListResponse?.[0]?.borrowerName ?? "NA"}`,
        });
        setOpenCallResponseModal(false);
        if (parsed.borrowerListResponse?.[0]) {
          setBorrowerViewDetails(parsed.borrowerListResponse[0]);
        }
        break;
      case "HANGUP":
        notification.success({
          message: "Connected Call",
          description: `📞 Call Disconnected With: ${parsed.borrowerListResponse?.[0]?.borrowerName ?? "NA"}`,
        });
        if (parsed.borrowerListResponse?.[0]) {
          setHangupBorrowerData(parsed);
          setOpenCallResponseModal(true);
        }
        break;
      default:
        console.log("Unknown Event:", parsed);
    }
  });

  es.addEventListener("heartbeat", () => {
    console.log("💓 Heartbeat");
  });

  es.onerror = (error) => {
    console.error(`❌ SSE Error (${type}):`, error);
    setIsStreaming(false);
    setActiveStreamType(null);
    es.close();
  };

  setEventSource(es);
};

const startStreamKnowlarity = () => {
  startStreamGeneric("stream/start", "knowlarity");
};

const startStream = () => {
  startStreamGeneric("stream/start-solveesy", "solveesy");
};

const stopStream = async () => {
  eventSource?.close();
  setEventSource(null);
  setIsStreaming(false);
  setActiveStreamType(null);
  console.log("SSE connection closed");

  try {
    await fetch(`${process.env.REACT_APP_API_BASE_URL}stream/stop`, {
      method: "POST",
      credentials: "include",
    });
  } catch (err) {
    console.error("Failed to call stop-stream API:", err);
  }
};
 

  const data: any = [
    {
      label1: <span className="font-bold">Loan ID</span>,
      value1: borrowerViewDetails?.loanId || "N/A",
      label2: <span className="font-bold">Lender</span>,
      value2: borrowerViewDetails?.clientName || "N/A",
      label3: <span className="font-bold">Product Name</span>,
      value3: borrowerViewDetails?.productName || "N/A",
    },
    {
      label1: <span className="font-bold">Date of Purchase</span>,
      value1: borrowerViewDetails?.dateOfPurchase,
      label2: <span className="font-bold">DPD Status</span>,
      value2: borrowerViewDetails?.status ? (
        <span style={{ color: getColorForStatus(borrowerViewDetails?.dpd ?? "") }}>
          {borrowerViewDetails.dpd || "N/A"}
        </span>
      ) : (
        "N/A"
      ),
      label3: <span className="font-bold">Loan Amount</span>,
      value3: borrowerViewDetails?.loanAmount
        ? formatRupee(borrowerViewDetails.loanAmount)
        : "N/A",
    },
    {
      label1: <span className="font-bold">Due Amount</span>,
      value1: borrowerViewDetails?.dueAmount
        ? formatRupee(borrowerViewDetails.dueAmount)
        : "N/A",
      label2: <span className="font-bold">Interest Amount</span>,
      value2: borrowerViewDetails?.interestAmount
        ? formatRupee(borrowerViewDetails?.interestAmount)
        : "N/A",
      label3: <span className="font-bold">Others Charges</span>,
      value3: borrowerViewDetails?.otherCharges
        ? formatRupee(borrowerViewDetails.otherCharges)
        : "N/A",
    },
    {
      label1: <span className="font-bold">Settlement Amount</span>,
      value1: borrowerViewDetails?.settlementAmount
        ? formatRupee(borrowerViewDetails?.settlementAmount)
        : "N/A",
      label2: <span className="font-bold">Waiver Amount</span>,
      value2: borrowerViewDetails?.waiverAmount
        ? formatRupee(borrowerViewDetails?.waiverAmount)
        : "N/A",
      label3: <span className="font-bold">Final Offer Amount</span>,
      value3: borrowerViewDetails?.finalOfferAmount
        ? formatRupee(borrowerViewDetails?.finalOfferAmount)
        : "N/A",
    },
    {
      label1: <span className="font-bold">DPD (Days)</span>,
      value1:
        borrowerViewDetails?.dpdCount != null
          ? `${borrowerViewDetails.dpdCount} Days`
          : "N/A",
      label2: <span className="font-bold">Borrower Age</span>,
      value2:
        borrowerViewDetails?.borrowerAge != null
          ? `${borrowerViewDetails.borrowerAge} Years`
          : "N/A",
      label3: <span className="font-bold">Address</span>,
      value3: (
        <div
          style={{
            maxWidth: "350px",
            whiteSpace: "normal",
            overflowWrap: "break-word",
            lineHeight: "20px",
          }}
        >
          {borrowerViewDetails?.address || "N/A"}
        </div>
      ),
    },
  ];

  const columns = [
    { title: "Label 1", dataIndex: "label1" },
    { title: "Value 1", dataIndex: "value1" },
    { title: "Label 2", dataIndex: "label2" },
    { title: "Value 2", dataIndex: "value2" },
    { title: "Label 3", dataIndex: "label3" },
    { title: "Value 3", dataIndex: "value3" },
  ];

  useEffect(() => {
    return () => {
      if (eventSource) eventSource.close();
    };
  }, [eventSource]);

  return (
    <div>
      <div className="flex justify-between gap-2 mb-4">
        <div className="flex font-bold">
          Borrower Name:{" "}
          <h1 className="ml-4 font-normal">
            {borrowerViewDetails?.borrowerName || "NA"}
          </h1>
        </div>
        <div className="flex justify-between gap-2 mb-4">
          <div className="flex items-center">
            {isStreaming && (
              <div className="relative w-4 h-4 mr-6">
                <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-green-600 shadow-[0_0_10px_2px_rgba(16,185,129,0.6)]"></span>
              </div>
            )}
        <Button
  type="primary"
  onClick={startStreamKnowlarity}
  disabled={activeStreamType === "knowlarity"}
  className="mr-3"
>
  {activeStreamType === "knowlarity" ? "Streaming..." : "Start Stream Knowlarity"}
</Button>

<Button
  type="primary"
  onClick={startStream}
  disabled={activeStreamType === "solveesy"}
  className="mr-3"
>
  {activeStreamType === "solveesy" ? "Streaming..." : "Start Stream Solveesy"}
</Button>

<Button danger onClick={stopStream} disabled={!isStreaming}>
  Stop Stream
</Button>
          </div>
        </div>
      </div>
      <AutoCallResponseModal
        caseId={caseId}
        open={openCallResponseModal}
        setOpenCallResponseModal={setOpenCallResponseModal}
        data={hangupBorrowerData}
      />
      <Table
        loading={isCaseDetailsPending}
        className="borrower-details-table"
        showHeader={false}
        columns={columns}
        dataSource={data}
        size="middle"
        pagination={false}
      />
    </div>
  );
};